"use client";

import { useDeferredValue, useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  codePoints,
  LATEX_SYMBOLS,
  type LatexSymbol,
  SYMBOL_CATEGORIES,
  type SymbolCategory,
  searchSymbols,
} from "@/lib/tool-kit/developer/latex-symbols";

/** English strings. A translated content file overrides these through `ui`. */
export const LATEX_SYMBOL_FINDER_STRINGS = {
  searchLabel: "Search by name, command or the symbol itself",
  searchPlaceholder: "arrow, \\leq, ≈ or U+2192",
  categoryLabel: "Browse a category",
  categories: {
    all: "All symbols",
    greek: "Greek letters",
    binary: "Binary operators",
    relation: "Relations",
    negated: "Negated relations",
    arrow: "Arrows",
    logic: "Sets and logic",
    big: "Big operators",
    delimiter: "Delimiters",
    accent: "Math accents",
    dots: "Dots",
    misc: "Miscellaneous math",
    letters: "Letter-like symbols and number sets",
    text: "Text symbols and letters",
    textAccent: "Text accents",
  },
  packages: {
    latex: "Built into LaTeX",
    amsmath: "\\usepackage{amsmath}",
    amssymb: "\\usepackage{amssymb}",
    mathtools: "\\usepackage{mathtools}",
    textcomp: "textcomp — built in since LaTeX 2020",
    fontenc: "\\usepackage[T1]{fontenc}",
    stmaryrd: "\\usepackage{stmaryrd}",
    esint: "\\usepackage{esint}",
    mathdots: "\\usepackage{mathdots}",
    mathrsfs: "\\usepackage{mathrsfs}",
    gensymb: "\\usepackage{gensymb}",
  },
  modes: {
    math: "Math mode",
    text: "Text mode",
  },
  resultCount: "one: {count} symbol|other: {count} symbols",
  searchingAll: "{results} across every category.",
  noResults:
    "No symbols match “{query}”. Try one word, part of the command, or paste the symbol.",
  copyCommand: "Copy command",
  copyCharacter: "Copy character",
  copied: "Copied!",
} as const;

type Strings = typeof LATEX_SYMBOL_FINDER_STRINGS;
type CategoryChoice = SymbolCategory | "all";

const CATEGORY_CHOICES: readonly CategoryChoice[] = [
  "all",
  ...SYMBOL_CATEGORIES,
];

function SymbolCard({
  symbol,
  strings,
}: {
  symbol: LatexSymbol;
  strings: Strings;
}) {
  return (
    <li className={`${CARD} flex flex-col gap-3`}>
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="min-w-12 text-center font-serif text-3xl text-foreground leading-none"
        >
          {symbol.char}
        </span>
        <div className="min-w-0 space-y-1">
          <code className="break-all font-mono text-foreground text-sm">
            {symbol.command}
          </code>
          <p className="text-foreground text-sm">{symbol.name}</p>
          <p className={HINT}>
            {codePoints(symbol.char)} · {strings.modes[symbol.mode]}
          </p>
          <p className={`${HINT} break-all font-mono`}>
            {strings.packages[symbol.package]}
          </p>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap gap-2">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyCommand}
          small
          text={symbol.command}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyCharacter}
          small
          text={symbol.char}
        />
      </div>
    </li>
  );
}

export default function LatexSymbolFinderWidget() {
  const strings = useToolUi(LATEX_SYMBOL_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryChoice>("greek");
  const deferred = useDeferredValue(query);

  const searching = deferred.trim() !== "";
  const symbols = searching
    ? searchSymbols(deferred)
    : LATEX_SYMBOLS.filter(
        (symbol) => category === "all" || symbol.category === category
      );
  const count = tPlural(strings.resultCount, locale, symbols.length, {
    count: fmt(symbols.length),
  }).trim();

  return (
    <section className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="lsym-search"
          label={strings.searchLabel}
          onChange={setQuery}
          placeholder={strings.searchPlaceholder}
          type="search"
          value={query}
        />
        <SelectField
          id="lsym-category"
          label={strings.categoryLabel}
          onChange={(value) => {
            setCategory(value);
            setQuery("");
          }}
          options={CATEGORY_CHOICES.map((value) => ({
            value,
            label: strings.categories[value],
          }))}
          value={category}
        />
      </div>
      <p aria-live="polite" className={HINT}>
        {searching ? t(strings.searchingAll, { results: count }) : count}
      </p>
      {symbols.length === 0 ? (
        <Notice>{t(strings.noResults, { query: deferred.trim() })}</Notice>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {symbols.map((symbol) => (
            <SymbolCard
              key={symbol.command}
              strings={strings}
              symbol={symbol}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
